// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import * as JsonCodec from "../primitives/JsonCodec.res.js";
import * as Navigation from "../primitives/Navigation.res.js";
import * as QueryString from "../primitives/QueryString.res.js";
import * as Primitive_int from "@rescript/runtime/lib/es6/Primitive_int.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Primitive_object from "@rescript/runtime/lib/es6/Primitive_object.js";
import * as Primitive_option from "@rescript/runtime/lib/es6/Primitive_option.js";
import * as OperationDurations from "./OperationDurations.res.js";

function totalPages(totalCount, edgesPerPage) {
  if (edgesPerPage <= 0 || totalCount <= 0) {
    return 1;
  } else if (Primitive_int.mod_(totalCount, edgesPerPage) === 0) {
    return Primitive_int.div(totalCount, edgesPerPage);
  } else {
    return Primitive_int.div(totalCount, edgesPerPage) + 1 | 0;
  }
}

function generateCursor(page, edgesPerPage, totalCount) {
  let maxPage = totalPages(totalCount, edgesPerPage);
  if (!(page >= 1 && page <= maxPage)) {
    return {
      TAG: "Error",
      _0: undefined
    };
  }
  let index = (page - 1 | 0) * edgesPerPage | 0;
  let cursor = "9252fe5d140e19d308f2037404a0536a" + index.toString();
  return {
    TAG: "Ok",
    _0: btoa(cursor)
  };
}

function connectionArguments(currentPage, previousPage, totalCount, cursors, edgesPerPageOpt, param) {
  let edgesPerPage = edgesPerPageOpt !== undefined ? edgesPerPageOpt : 50;
  if (currentPage === 1) {
    return {
      first: edgesPerPage
    };
  }
  let startCursor = cursors[0];
  let exit = 0;
  if (startCursor !== undefined) {
    if (currentPage === (previousPage - 1 | 0)) {
      return {
        last: edgesPerPage,
        before: startCursor
      };
    }
    if (currentPage === (previousPage - 2 | 0)) {
      let startCursor$1 = generateCursor(currentPage, edgesPerPage, totalCount);
      if (startCursor$1.TAG === "Ok") {
        return {
          last: edgesPerPage,
          before: startCursor$1._0
        };
      } else {
        return;
      }
    }
    exit = 2;
  } else {
    exit = 2;
  }
  if (exit === 2) {
    let endCursor = cursors[1];
    if (endCursor !== undefined) {
      if (currentPage === (previousPage + 1 | 0)) {
        return {
          first: edgesPerPage,
          after: endCursor
        };
      }
      if (currentPage === (previousPage + 2 | 0)) {
        let endCursor$1 = generateCursor(currentPage, edgesPerPage, totalCount);
        if (endCursor$1.TAG === "Ok") {
          return {
            first: edgesPerPage,
            after: endCursor$1._0
          };
        } else {
          return;
        }
      }
    }
  }
  if (currentPage === totalPages(totalCount, edgesPerPage)) {
    return {
      last: edgesPerPage
    };
  }
}

function initialState(firstOpt, filters, sorting, extra, param) {
  let first = firstOpt !== undefined ? firstOpt : 50;
  return {
    currentPage: 1,
    previousPage: -1,
    filters: filters,
    sorting: sorting,
    extra: extra,
    connectionArguments: {
      first: first
    }
  };
}

function reducer(state, action) {
  let match = state.connectionArguments;
  let edgesPerPage = match.first;
  let edgesPerPage$1;
  if (edgesPerPage !== undefined) {
    edgesPerPage$1 = edgesPerPage;
  } else {
    let edgesPerPage$2 = match.last;
    edgesPerPage$1 = edgesPerPage$2 !== undefined ? edgesPerPage$2 : 50;
  }
  switch (action.TAG) {
    case "Navigated" :
      let nextPage = action.nextPage;
      let previousPage = state.currentPage;
      let maybeConnectionArguments = connectionArguments(nextPage, previousPage, action.totalCount, action.cursors, edgesPerPage$1, undefined);
      if (maybeConnectionArguments === undefined) {
        return state;
      }
      let newrecord = {...state};
      newrecord.connectionArguments = maybeConnectionArguments;
      newrecord.previousPage = previousPage;
      newrecord.currentPage = nextPage;
      return newrecord;
    case "Searched" :
      let searchQuery = action._0;
      let tmp = searchQuery === "" ? undefined : searchQuery;
      return {
        currentPage: 1,
        previousPage: -1,
        searchQuery: tmp,
        filters: state.filters,
        sorting: state.sorting,
        extra: state.extra,
        connectionArguments: {
          first: edgesPerPage$1
        }
      };
    case "FiltersUpdated" :
      return {
        currentPage: 1,
        previousPage: -1,
        searchQuery: state.searchQuery,
        filters: action._0(state.filters),
        sorting: state.sorting,
        extra: state.extra,
        connectionArguments: {
          first: edgesPerPage$1
        }
      };
    case "SortingUpdated" :
      return {
        currentPage: 1,
        previousPage: -1,
        searchQuery: state.searchQuery,
        filters: state.filters,
        sorting: action._0,
        extra: state.extra,
        connectionArguments: {
          first: edgesPerPage$1
        }
      };
    case "ExtraUpdated" :
      let newrecord$1 = {...state};
      newrecord$1.extra = Primitive_option.some(action._0);
      return newrecord$1;
    case "Reset" :
      return action._0;
  }
}

function nextPage(state, action, totalPages) {
  let match = state.currentPage;
  if ((match === 0 || match === 1) && (totalPages === 0 || totalPages === 1)) {
    return;
  }
  switch (action) {
    case "First" :
      if (match >= 1) {
        return 1;
      } else {
        return;
      }
    case "PrevPrev" :
      if (match >= 1) {
        return match - 2 | 0;
      } else {
        return;
      }
    case "Prev" :
      if (match > totalPages) {
        return totalPages;
      } else if (match > 1) {
        return match - 1 | 0;
      } else {
        return;
      }
    case "Next" :
      if (match > totalPages) {
        return totalPages;
      } else if (match >= 1 && match < totalPages) {
        return match + 1 | 0;
      } else {
        return;
      }
    case "NextNext" :
      if (match >= 1 && match < (totalPages - 1 | 0)) {
        return match + 2 | 0;
      } else {
        return;
      }
    case "Last" :
      if (match >= 1) {
        return totalPages;
      } else {
        return;
      }
  }
}

function encoder(param) {
  let connectionArguments = param.connectionArguments;
  return [
    param.currentPage,
    param.previousPage,
    param.searchQuery,
    Primitive_option.some(param.filters),
    Primitive_option.some(param.sorting),
    param.extra,
    connectionArguments.first,
    connectionArguments.last,
    connectionArguments.after,
    connectionArguments.before
  ];
}

function value(filtersJsonCodec, sortingJsonCodec, extraJsonCodec, initialFilters, initialSorts, initialExtra) {
  return JsonCodec.object10(encoder, state => ({
    TAG: "Ok",
    _0: {
      currentPage: Stdlib_Option.getOr(state[0], 1),
      previousPage: Stdlib_Option.getOr(state[1], -1),
      searchQuery: state[2],
      filters: Stdlib_Option.getOr(state[3], initialFilters),
      sorting: Stdlib_Option.getOr(state[4], initialSorts),
      extra: Stdlib_Option.orElse(state[5], initialExtra),
      connectionArguments: {
        first: state[6],
        last: state[7],
        after: state[8],
        before: state[9]
      }
    }
  }), JsonCodec.optional(JsonCodec.field("page", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("previousPage", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("search", JsonCodec.string)), JsonCodec.optional(JsonCodec.field("filters", filtersJsonCodec)), JsonCodec.optional(JsonCodec.field("sorting", sortingJsonCodec)), JsonCodec.optional(JsonCodec.field("extra", extraJsonCodec)), JsonCodec.optional(JsonCodec.field("first", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("last", JsonCodec.int)), JsonCodec.optional(JsonCodec.field("after", JsonCodec.string)), JsonCodec.optional(JsonCodec.field("before", JsonCodec.string)));
}

function use(initialState, filtersJsonCodec, sortingJsonCodec, extraJsonCodec, param) {
  let codec = value(filtersJsonCodec, sortingJsonCodec, extraJsonCodec, initialState.filters, initialState.sorting, initialState.extra);
  let navigate = Navigation.useNavigate();
  let url = Navigation.useUrl();
  let initialState$1 = React.useMemo(() => {
    let urlState = JsonCodec.decodeWith(QueryString.parse(url.query), codec);
    let match = url.query;
    if (urlState.TAG === "Ok" && match !== "") {
      return urlState._0;
    } else {
      return initialState;
    }
  }, []);
  let match = React.useReducer(reducer, initialState$1);
  let state = match[0];
  React.useEffect(() => {
    let query = QueryString.stringify(JsonCodec.encodeWith(state, codec));
    let route = url.pathname + "?" + query;
    if (query !== url.query) {
      navigate({
        replace: true
      }, route);
    }
  }, [state]);
  return [
    state,
    match[1]
  ];
}

function loadingInteraction(previous, current) {
  if (Primitive_object.notequal(previous.searchQuery, current.searchQuery)) {
    return "search";
  } else if (Primitive_object.notequal(previous.connectionArguments, current.connectionArguments) && Primitive_object.equal(previous.filters, current.filters) && Primitive_object.equal(previous.sorting, current.sorting) && Primitive_object.equal(previous.extra, current.extra)) {
    return "paginate";
  } else {
    return "filter";
  }
}

function useLoadingInteraction(state, operations) {
  let operationsKey = Stdlib_Option.map(operations, operations => operations.map(OperationDurations.key).join(","));
  let previousRef = React.useRef(undefined);
  let interaction = React.useMemo(() => {
    let match = previousRef.current;
    if (match !== undefined && Primitive_object.equal(match[1], operationsKey)) {
      return loadingInteraction(match[0], state);
    } else {
      return "initial";
    }
  }, [
    state,
    operationsKey
  ]);
  React.useEffect(() => {
    previousRef.current = [
      state,
      operationsKey
    ];
  }, [
    state,
    operationsKey
  ]);
  return interaction;
}

let defaultEdgesPerPage = 50;

export {
  defaultEdgesPerPage,
  totalPages,
  connectionArguments,
  initialState,
  reducer,
  nextPage,
  use,
  loadingInteraction,
  useLoadingInteraction,
}
/* react Not a pure module */
